import {
  createContext,
  type FC,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { datadogRum } from "@datadog/browser-rum";
import type {
  ExperimentPayloadsType,
  ExperimentState,
  ExperimentsType,
  IApiConfig,
  TrackingPropertiesType,
} from "@hopper-b2b/types";
import { initExperimentState } from "./utils/initExperimentState";

export const HIDE_CC = "ctr-marketplace-hidecreditcard";

const defaultInitState: ExperimentState = {
  experiments: {},
  experimentPayloads: {},
};

export const ExperimentsContext =
  createContext<ExperimentState>(defaultInitState);

type ExperimentsProviderProps = {
  apiConfig: IApiConfig;
  initState?: ExperimentState;
  isLoggedIn?: boolean;
  partnerExperiments?: string;
  refreshNonce?: number;
};

const initDatadogFeatureFlags = (state: ExperimentState) => {
  Object.keys(state.experiments).forEach((key) => {
    datadogRum.addFeatureFlagEvaluation(key, state.experiments[key]);
  });
};

export const ExperimentsProvider: FC<
  ExperimentsProviderProps & PropsWithChildren
> = ({
  apiConfig,
  initState = defaultInitState,
  isLoggedIn = false,
  children,
  partnerExperiments,
  refreshNonce = 0,
}) => {
  const [experiments, setExperiments] = useState<ExperimentsType>(
    initState.experiments
  );
  const [experimentPayloads, setExperimentPayloads] =
    useState<ExperimentPayloadsType>(initState.experimentPayloads);
  const [trackingProperties, setTrackingProperties] =
    useState<TrackingPropertiesType>(initState.trackingProperties ?? {});

  const shouldFetchExperiments = useMemo(
    (): boolean => !initState?.experiments?.size && isLoggedIn,
    [initState?.experiments?.size, isLoggedIn]
  );

  const lastRefreshNonceRef = useRef<number>(refreshNonce);

  // SEC-007 / SEC-008 mitigation: only start in loading state when
  // a fetch will actually be issued. If `shouldFetchExperiments`
  // is false (pre-seeded experiments via `initState`, or user not
  // yet logged in), the `.finally()` below never runs and consumers
  // would see an infinite loader.
  //
  // Lazy initializer so the value is computed once at mount; the
  // useEffect below also keeps it consistent if `shouldFetchExperiments`
  // flips after mount (e.g. user logs in mid-session).
  const [isLoading, setIsLoading] = useState<boolean>(
    () => shouldFetchExperiments
  );

  // Monotonic "flag values have settled" signal. `isLoading` alone
  // cannot distinguish "fetch not started yet" from "fetch done"
  // (both read `false`), which is a real race for consumers that
  // mount in the same commit that flips `isLoggedIn`, e.g. the
  // NU-470 post-login routing gate, which must not evaluate a rollout
  // flag before the values are final.
  //
  // Same SEC-007 / SEC-008 shape as `isLoading` above: when no fetch
  // will be issued because experiments were pre-seeded via
  // `initState`, the `.finally()` below never runs, so the seeded
  // values must count as settled at mount or consumers gating on
  // this signal would hang forever.
  const [isFetched, setIsFetched] = useState<boolean>(
    () =>
      !shouldFetchExperiments &&
      Object.keys(initState.experiments ?? {}).length > 0
  );

  const value: ExperimentState = useMemo(
    () => ({
      experiments,
      experimentPayloads,
      trackingProperties,
      isLoading,
      isFetched,
    }),
    [experiments, experimentPayloads, trackingProperties, isLoading, isFetched]
  );

  useEffect(() => {
    const refreshRequested = refreshNonce !== lastRefreshNonceRef.current;

    if (!shouldFetchExperiments && !refreshRequested) {
      return;
    }

    if (shouldFetchExperiments || refreshRequested) {
      setIsLoading(true);
    }

    // Explicitly re-enter loading state whenever we truly need to
    // refetch — either because the user just became authenticated
    // (`shouldFetchExperiments`) or because the caller asked for a
    // refresh after rotating the feature-flag session cookie. Without
    // this, the lazy initializer above only catches the value at mount
    // and consumers never see a loading signal for post-mount fetches.
    initExperimentState(
      shouldFetchExperiments || refreshRequested,
      apiConfig,
      defaultInitState,
      partnerExperiments
    )
      .then((newState: ExperimentState) => {
        setExperiments(
          (currentState: ExperimentsType) =>
            ({
              ...currentState,
              ...newState.experiments,
              ...(partnerExperiments ? { partnerExperiments } : {}),
            }) as ExperimentsType
        );
        setExperimentPayloads(
          (currentState: ExperimentsType) =>
            ({
              ...currentState,
              ...newState.experimentPayloads,
            }) as ExperimentsType
        );
        setTrackingProperties(
          (currentState: ExperimentsType) =>
            ({
              ...currentState,
              ...newState.trackingProperties,
              ...(partnerExperiments ? { partnerExperiments } : {}),
            }) as ExperimentsType
        );
        initDatadogFeatureFlags(newState);
      })
      .catch((error) => {
        // A failed fetch already surfaces to consumers as empty
        // experiments with `isLoading` false / `isFetched` true;
        // catching here just keeps the rejection out of the global
        // unhandled-rejection handler while staying loud in logs.
        console.error("[ExperimentsProvider] experiments fetch failed", error);
      })
      .finally(() => {
        // Always flip loading off so consumers don't hang on the
        // loader if the fetch fails or returns nothing.
        setIsLoading(false);
        setIsFetched(true);
        lastRefreshNonceRef.current = refreshNonce;
      });
  }, [apiConfig, shouldFetchExperiments, partnerExperiments, refreshNonce]);

  return <ExperimentsContext.Provider value={value} children={children} />;
};
